import { betbyApi } from "@/betby/api/services";
import type { BetbyInitInfo } from "@/betby/api/types";
import {
  getCachedBetbyToken,
  getCachedInitInfo,
  cacheSessionTicket,
  cacheBetbyToken,
  requestNewSessionTicket,
} from "@/betby/models/auth/sessionTicketCache";
import { resolveSessionTicket } from "@/betby/models/auth/authSessionResolver";
import { sendTokenExpiredMessage } from "@/betby/models/host/unityMessageService";
import { getHostBridge } from "@/betby/adapters/host/hostBridge";
import LogUtil from "@/betby/utils/LogUtil";

interface GetFreshBetbyTokenOptions {
  currentMode: "gc" | "sc";
  setInitInfoIfChanged: (nextInfo: BetbyInitInfo) => void;
  markSkipCleanup: () => void;
  resolveInitInfoFromResponse: (
    responseData: unknown,
    mode: string,
  ) => BetbyInitInfo | null;
}

/**
 * Get fresh betby token when SDK's onTokenExpired callback is triggered
 *
 * Flow:
 * 1. Try to request new session ticket from host (refresh cache)
 * 2. If no host support, resolve session ticket from existing sources and cache it
 * 3. Fetch new init info with session ticket
 * 4. Cache the new betby token (refresh cache)
 */
export async function getFreshBetbyToken({
  currentMode,
  setInitInfoIfChanged,
  markSkipCleanup,
  resolveInitInfoFromResponse,
}: GetFreshBetbyTokenOptions): Promise<string> {
  LogUtil.info("[BetbyTokenService] onTokenExpired triggered, refreshing caches");

  // 1. Try to request new session ticket from host (this will refresh cache)
  const bridge = getHostBridge();
  let sessionTicket: string | null = null;

  if (bridge?.requestSessionTicket) {
    LogUtil.info("[BetbyTokenService] Requesting new session ticket from host...");
    sessionTicket = await requestNewSessionTicket();
    if (sessionTicket) {
      LogUtil.info("[BetbyTokenService] Got new session ticket from host, cache refreshed");
    } else {
      LogUtil.warn("[BetbyTokenService] Failed to get new session ticket from host");
    }
  }

  // 2. If no new ticket from host, resolve from existing sources and refresh cache
  if (!sessionTicket) {
    sessionTicket = resolveSessionTicket();
    if (sessionTicket) {
      cacheSessionTicket(sessionTicket);
      LogUtil.info("[BetbyTokenService] Session ticket resolved from existing sources, cache refreshed");
    }
  }

  // 3. Check if we have a valid session ticket
  if (!sessionTicket) {
    LogUtil.warn("[BetbyTokenService] No session ticket available");
    sendTokenExpiredMessage();
    return "";
  }

  // 4. Fetch new init info
  try {
    const response = await betbyApi.getInitInfo({
      options: { skipGlobalErrorHandler: true },
    });

    if (response.code === 200 && response.data?.bet_by_init_info?.length) {
      const matched = resolveInitInfoFromResponse(response.data, currentMode);
      if (!matched) {
        return "";
      }

      // 5. Cache the new token (refresh cache with new timestamp)
      cacheBetbyToken(matched.bet_by_token, currentMode, matched);
      LogUtil.info("[BetbyTokenService] Betby token cache refreshed");

      markSkipCleanup();
      setInitInfoIfChanged(matched);
      return matched.bet_by_token;
    }

    if (response.code === 401) {
      LogUtil.warn("[BetbyTokenService] 401 - session ticket invalid");
      sendTokenExpiredMessage();
      return "";
    }

    LogUtil.warn(
      "[BetbyTokenService] getInitInfo failed with code:",
      response.code,
    );
    return "";
  } catch (error) {
    LogUtil.error("[BetbyTokenService] Fetch init info error:", error);
    return "";
  }
}

/**
 * Get betby token on initial load
 * This should be called after session ticket is set
 */
export async function getInitialBetbyToken(
  currentMode: "gc" | "sc",
  sessionTicket: string,
): Promise<BetbyInitInfo | null> {
  // Cache the session ticket
  cacheSessionTicket(sessionTicket);

  // Check if we have cached token for this mode
  const cachedToken = getCachedBetbyToken(currentMode);
  const cachedInitInfo = getCachedInitInfo(currentMode);

  if (cachedToken && cachedInitInfo) {
    LogUtil.info("[BetbyTokenService] Using cached token on initial load");
    return {
      brand_id: cachedInitInfo.brand_id,
      bet_by_token: cachedToken,
      theme_name: cachedInitInfo.theme_name,
      lang: cachedInitInfo.lang,
      money_type: cachedInitInfo.money_type,
    };
  }

  // Fetch fresh init info
  try {
    const response = await betbyApi.getInitInfo({
      options: { skipGlobalErrorHandler: true },
    });

    if (response.code === 200 && response.data?.bet_by_init_info?.length) {
      const initInfoList = response.data.bet_by_init_info as BetbyInitInfo[];
      const matched = initInfoList.find(
        (info) => info.money_type.toLowerCase().includes(currentMode),
      ) ?? initInfoList[0];

      if (matched) {
        // Cache the token
        cacheBetbyToken(matched.bet_by_token, currentMode, matched);
        return matched;
      }
    }

    return null;
  } catch (error) {
    LogUtil.error("[BetbyTokenService] Initial fetch error:", error);
    return null;
  }
}